import { uid } from 'uid';
import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dispatchEvent, navigateDropdown } from '@fil-global/shared';

// TODO delete fil-icon
import '@fil-global/fil-icon';
import '@fil-global/fil-button';

import componentStyles from './fil-dropdown.styles.js';

/**
 * @tag fil-dropdown
 * @property {"left"|"right"} [alignment='left'] Alignment of the dropdown menu of the component
 * @property {'icon' | 'primary' | 'secondary' | 'tertiary'} [kind="primary"] Type of the button in the dropdown.
 * @property {"standard" | "small"} [size="standard"] Size of the component
 * @property {boolean} [open=false] Open state of the component
 * @property {array} [options=[]] Array of the options in the component
 * @property {string} [label] Label of the component
 * @property {'auto' | 'down' | 'up'} [position] Position of the component
 * @fires change.gds.dropdown (event: Event) => void
 */
export class FilDropdown extends LitElement {
  static properties = {
    alignment: { type: String }, // 'left' | 'right'
    kind: { type: String }, // 'icon' | 'primary' | 'secondary' | 'tertiary'
    label: { type: String },
    open: { type: Boolean },
    options: { type: Array },
    position: { type: String }, // 'auto' | 'down' | 'up'
    size: { type: String }, // 'small' | 'standard'
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.alignment = 'left';
    this.kind = 'secondary';
    this.open = false;
    this.position = 'down';
    this.size = 'standard';

    this.boundListener = this.windowHandler.bind(this);
    this.uid = uid();
  }

  collisionDetection() {
    const dropdown = this.shadowRoot.querySelector('.fil-dropdown-options');
    if (dropdown) {
      const off = dropdown.getBoundingClientRect();
      const docH = window.innerHeight;
      const isEntirelyVisible = off.top + off.height <= docH;
      this.position = isEntirelyVisible ? 'down' : 'up';
    }
  }

  windowHandler(event) {
    if (this.open) {
      navigateDropdown(event, this.shadowRoot.querySelector('.fil-dropdown-options'));
    }
    if (event.detail?.id === this.uid) {
      return;
    }
    if (event.type === 'click' || event.keyCode === 27) {
      this.open = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this.boundListener);
    window.addEventListener('keydown', this.boundListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('click', this.boundListener);
    window.removeEventListener('keydown', this.boundListener);
  }

  onClick(event) {
    this.collisionDetection();
    this.open = !this.open;

    const value = event.target.getAttribute('data-value');
    dispatchEvent(this, 'change.gds.dropdown', { value });
  }

  onKeypress(event) {
    // check for key 'enter'
    if (event.keyCode === 13) {
      event.preventDefault();
      this.onClick(event);
    }
  }

  renderOption(option) {
    switch (option.type) {
      case 'button':
        return html`<span
          class="fil-dropdown--button"
          aria-label="${option.label || 'button'}"
          @click=${this.onClick}
          @keypress=${this.onKeypress}
          data-value="${option.value}"
          tabindex="0"
        >
          ${option.icon
            ? html`<fil-icon
                icon="${option.icon}"
                size="${this.size === 'small' ? 16 : 24}"
              ></fil-icon>`
            : null}${option.label}
        </span>`;
      case 'link':
        return html`<a
          class="fil-dropdown--link"
          href="${option.url}"
          target="${ifDefined(option.target)}"
          tabindex="0"
          >${option.icon
            ? html`<fil-icon
                icon="${option.icon}"
                size="${this.size === 'small' ? 16 : 24}"
              ></fil-icon>`
            : null}${option.label}</a
        >`;
      default:
        return html`<div>undefined option.type</div>`;
    }
  }

  render() {
    const classes = {
      [`fil-dropdown--open`]: this.open,
      [`fil-dropdown-wrapper--${this.size}`]: this.size !== 'standard',
      [`fil-dropdown--${this.alignment}`]: this.alignment,
      [`fil-dropdown--${this.kind}`]: this.kind,
      [`fil-dropdown--${this.open ? this.position : 'down'}`]: this.open,
    };
    const icon = this.open ? 'custom-chevron-up' : 'custom-chevron-down';

    return html`<div
      class="fil-dropdown-wrapper${classMap(classes)}"
      @click=${event => {
        event.stopPropagation();
      }}
    >
      <fil-button
        label="${ifDefined(this.label)}"
        kind="${this.kind}"
        icon="${icon}"
        iconPosition="right"
        iconSize="${this.size === 'small' ? 8 : 12}"
        size="${this.size}"
        id="${this.uid}"
        aria-haspopup="listbox"
        @click=${() => {
          this.collisionDetection();
          this.open = !this.open;
        }}
      ></fil-button>
      <ul
        class="fil-dropdown-options"
        role="listbox"
        title="dropdown list"
        aria-labelledby="${this.uid}"
      >
        ${this.options &&
        this.options.map(
          option =>
            html`<li role="option" title="${option.label}">${this.renderOption(option)}</li>`,
        )}
      </ul>
    </div>`;
  }
}

export default FilDropdown;
